import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  TableSortLabel,
  Divider,
  LinearProgress,
  Fade,
  Grow,
  Zoom,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';

import {
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Clear,
  Assignment,
  CalendarToday,
  Verified,
  Person,
  Business,
  Category,
  ThumbUp,
  LocalShipping,
  Receipt,
  Inventory,
  PersonAdd,
  AssignmentTurnedIn,
  LocationOn,
  Description,
  Warning,
  Storefront,
  CheckCircleOutline,
  HourglassEmpty,
  ErrorOutline,
  InfoOutlined,
  TrendingUp,
  TrendingDown,
  Warehouse,
  QrCode,
  DoneAll,
  Schedule,
  Block,
  Print
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/sidebar_requisitionstaff';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 800,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  }
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: '20px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '28px',
  ...(status === 'Approved by GM' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Approved by Dept Head' && {
    backgroundColor: alpha(theme.palette.info.main, 0.12),
    color: theme.palette.info.main,
    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
  }),
  ...(status === 'Issued' && {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  }),
  ...(status === 'Received' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Pending' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.main,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
}));

const StockIndicator = styled(Box)(({ theme, stocklevel }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '12px',
  backgroundColor: stocklevel === 'low' 
    ? alpha(theme.palette.error.main, 0.1)
    : stocklevel === 'medium'
    ? alpha(theme.palette.warning.main, 0.1)
    : alpha(theme.palette.success.main, 0.1),
  border: `1px solid ${
    stocklevel === 'low' 
      ? alpha(theme.palette.error.main, 0.3)
      : stocklevel === 'medium'
      ? alpha(theme.palette.warning.main, 0.3)
      : alpha(theme.palette.success.main, 0.3)
  }`,
}));

const RequisitionManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userDepartment, setUserDepartment] = useState('');
  const [userPhone, setUserPhone] = useState('');

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Stock validation states
  const [stockInfo, setStockInfo] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    department: '',
    item_name: '',
    quantity_requested: '',
    unit_of_measurement: '',
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState({
    quantity_requested: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const unitOptions = ['pcs', 'kg', 'litres', 'bunches', 'packs', 'boxes', 'reams', 'units'];

  // Fetch stock availability for selected item
  const fetchStockAvailability = useCallback(async (itemName) => {
    if (!itemName) {
      setStockInfo(null);
      setStockError(null);
      return;
    }
    
    setStockLoading(true);
    setStockError(null);
    try {
      console.log('Fetching stock for item:', itemName);
      
      const response = await axios.get(`${API_BASE_URL}/inventory/get_item_stock.php`, {
        params: { item_name: itemName },
        timeout: 10000
      });
      
      console.log('Stock response:', response.data);
      
      if (response.data.success && response.data.stock_info) {
        setStockInfo(response.data.stock_info);
        setStockError(null);
      } else {
        setStockInfo(null);
        setStockError(response.data.message || 'Item not found in inventory');
      }
    } catch (error) {
      console.error('Error fetching stock info:', error);
      setStockInfo(null);
      
      if (error.code === 'ECONNABORTED') {
        setStockError('Request timeout. Please check your connection.');
      } else if (error.response) {
        setStockError(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        setStockError('Cannot connect to server. Please check your network.');
      } else {
        setStockError(error.message || 'Could not fetch stock information');
      }
    } finally {
      setStockLoading(false);
    }
  }, []);

  // Fetch departments from depts_list table
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/departments/get_departments.php`);
      if (response.data.success) {
        setDepartments(response.data.departments);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  // Fetch items list
  const fetchItemsList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/get_item_names.php`);
      if (response.data.success) {
        setItemsList(response.data.items);
      }
    } catch (error) {
      console.error('Error fetching items list:', error);
    }
  }, []);

  // Fetch requisitions - Filter by department (show all requisitions from user's department)
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      // Filter by user's department instead of all departments
      if (userDepartment && userDepartment !== 'Not Assigned' && userDepartment !== '') {
        params.append('department', userDepartment);
      }
      
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_requisitions.php?${params}`);
      if (response.data.success) {
        setRequisitions(response.data.data);
        setTotalRequisitions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch requisitions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, userDepartment]);

  useEffect(() => {
    if (userDepartment && userDepartment !== 'Not Assigned' && userDepartment !== '') {
      fetchRequisitions();
    }
    fetchDepartments();
    fetchItemsList();
  }, [fetchRequisitions, fetchDepartments, fetchItemsList, userDepartment]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const role = localStorage.getItem('role');
    const department = localStorage.getItem('userDepartment');
    const phone = localStorage.getItem('phone');
    
    setLoggedInUser(userName || userEmail || 'System User');
    setUserRole(role || 'staff');
    setUserDepartment(department || '');
    setUserPhone(phone || '');
    
    if (department && department !== 'Not Assigned' && department !== '') {
      setFormData(prev => ({ ...prev, department: department }));
    }
  }, []);

  // Validate quantity before submission
  const validateQuantity = useCallback((quantity, availableStock) => {
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      return 'Quantity must be greater than 0';
    }
    if (availableStock !== null && qty > availableStock) {
      return `Insufficient stock. Available: ${availableStock.toLocaleString()} ${formData.unit_of_measurement || ''}`;
    }
    return '';
  }, [formData.unit_of_measurement]);

  // Handle quantity change with validation
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, quantity_requested: value });
    
    if (stockInfo) {
      const error = validateQuantity(value, stockInfo.available_stock);
      setFormErrors({ ...formErrors, quantity_requested: error });
    }
  };

  // Check if user can make requisition (department must be assigned)
  const canMakeRequisition = () => {
    return userDepartment && userDepartment !== 'Not Assigned' && userDepartment !== '';
  };

  // Add new requisition with stock validation
  const handleAddRequisition = async () => {
    // Check if department is assigned
    if (!canMakeRequisition()) {
      setSnackbar({
        open: true,
        message: 'Cannot create requisition. Please complete your HR staff form to have your department assigned.',
        severity: 'error'
      });
      return;
    }

    if (!formData.item_name || !formData.quantity_requested) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    // Final stock validation before submission
    if (stockInfo && parseFloat(formData.quantity_requested) > stockInfo.available_stock) {
      setSnackbar({
        open: true,
        message: `Insufficient stock! Available: ${stockInfo.available_stock} ${stockInfo.unit_of_measurement}`,
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        department: userDepartment,
        item_name: formData.item_name,
        quantity_requested: parseFloat(formData.quantity_requested),
        unit_of_measurement: formData.unit_of_measurement,
        requested_by: loggedInUser,
        notes: formData.notes || null
      };

      const response = await axios.post(`${API_BASE_URL}/inventory/add_requisition.php`, payload);
      
      if (response.data.success) {
        let successMessage = response.data.message || 'Requisition submitted successfully';
        if (response.data.warning) {
          successMessage += ' ' + response.data.warning;
        }
        setSnackbar({
          open: true,
          message: successMessage,
          severity: response.data.warning ? 'warning' : 'success'
        });
        handleCloseAddDialog();
        fetchRequisitions();
        resetForm();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding requisition:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to add requisition',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Update requisition status
  const handleUpdateStatus = async () => {
    setLoading(true);
    try {
      const payload = {
        id: selectedRequisition.id,
        action: selectedAction,
        user_name: loggedInUser
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/update_requisition_status.php`,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        handleCloseActionDialog();
        fetchRequisitions();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to update status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = requisitions.map(item => ({
      'ID': item.id,
      'Department': item.department,
      'Item Name': item.item_name,
      'Quantity': item.quantity_requested,
      'Unit': item.unit_of_measurement || 'N/A',
      'Requested By': item.requested_by,
      'Request Date': item.request_date,
      'Approved By Dept Head': item.approved_by_dept_head || 'Pending',
      'Approved By GM': item.approved_by_GM || 'Pending',
      'Issued By': item.issued_by || 'Pending',
      'Received By': item.received_by || 'Pending',
      'Status': item.status,
      'Notes': item.notes || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Requisitions');
    XLSX.writeFile(wb, `requisitions_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    if (!canMakeRequisition()) {
      setSnackbar({
        open: true,
        message: 'Cannot create requisition. Please complete your HR staff form to have your department assigned.',
        severity: 'error'
      });
      return;
    }
    resetForm();
    setAddDialogOpen(true);
  };

  const handleOpenViewDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setViewDialogOpen(true);
  };

  const handleOpenActionDialog = (requisition, action) => {
    setSelectedRequisition(requisition);
    setSelectedAction(action);
    setActionDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    resetForm();
    setStockInfo(null);
    setStockError(null);
    setFormErrors({ quantity_requested: '' });
  };
  
  const handleCloseViewDialog = () => setViewDialogOpen(false);
  const handleCloseActionDialog = () => setActionDialogOpen(false);

  const resetForm = () => {
    setFormData({
      department: userDepartment || '',
      item_name: '',
      quantity_requested: '',
      unit_of_measurement: '',
      notes: ''
    });
    setStockInfo(null);
    setStockError(null);
    setFormErrors({ quantity_requested: '' });
  };

  const handleItemSelect = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        item_name: newValue.item_name,
        unit_of_measurement: newValue.unit_of_measurement || ''
      });
      fetchStockAvailability(newValue.item_name);
      setFormErrors({ ...formErrors, quantity_requested: '' });
    } else {
      setFormData({
        ...formData,
        item_name: '',
        unit_of_measurement: ''
      });
      setStockInfo(null);
      setStockError(null);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'request_date' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setPage(0);
    handleFilterClose();
  };

  // Get action buttons based on user role and current status
 // Get action buttons based on user role and current status
const getActionButtons = (requisition) => {
  const buttons = [];
  
  // View details button is always available
  buttons.push({ action: 'view', label: 'View Details', icon: <Visibility />, color: 'info', isView: true });
  
  // Receive button - only when items are issued but not yet received by the requester
  // And only if the logged-in user is the requester
  if (requisition.issued_by && 
      !requisition.received_by && 
      requisition.requested_by === loggedInUser) {
    buttons.push({ 
      action: 'receive', 
      label: 'Confirm Receipt', 
      icon: <AssignmentTurnedIn />, 
      color: 'success' 
    });
  }
  
  return buttons;
};
  // Get stock level indicator
  const getStockLevel = (available, threshold) => {
    if (available <= threshold) return 'low';
    if (available <= threshold * 2) return 'medium';
    return 'high';
  };

  const statistics = {
    total: totalRequisitions,
    pending: requisitions.filter(r => !r.approved_by_dept_head && !r.approved_by_GM).length,
    approved: requisitions.filter(r => r.approved_by_GM && !r.issued_by).length,
    issued: requisitions.filter(r => r.issued_by && !r.received_by).length,
    completed: requisitions.filter(r => r.received_by).length
  };

  // Render Compact Requisitions Table
  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'requested_by'}
                direction={orderBy === 'requested_by' ? order : 'asc'}
                onClick={() => handleSort('requested_by')}
              >
                Requested By
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel
                active={orderBy === 'quantity_requested'}
                direction={orderBy === 'quantity_requested' ? order : 'asc'}
                onClick={() => handleSort('quantity_requested')}
              >
                Qty
              </TableSortLabel>
            </TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'request_date'}
                direction={orderBy === 'request_date' ? order : 'asc'}
                onClick={() => handleSort('request_date')}
              >
                Request Date
              </TableSortLabel>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : sortData(requisitions).length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  No requisitions found for {userDepartment} department
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(requisitions).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Person sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2">{item.requested_by}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Assignment sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_requested).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.request_date}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip label={item.status} status={item.status} size="small" />
                </TableCell>
                <TableCell align="center">
                  {getActionButtons(item).map((btn) => (
                    <Tooltip key={btn.action} title={btn.label}>
                      <IconButton 
                        size="small" 
                        color={btn.color}
                        onClick={() => btn.isView ? handleOpenViewDialog(item) : handleOpenActionDialog(item, btn.action)}
                      >
                        {btn.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 50, 100]}
        component="div"
        count={totalRequisitions}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3 }}>
          {/* Department Warning Banner */}
          {!canMakeRequisition() && (
            <Alert 
              severity="warning" 
              icon={<Warning />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Department Not Assigned
              </Typography>
              <Typography variant="body2">
                You cannot create requisitions until your department is assigned. 
                Please complete your HR staff form to have your department assigned.
              </Typography>
            </Alert>
          )}

          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Zoom in={true} style={{ transitionDelay: '0ms' }}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Total Requisitions
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1}>
                          {statistics.total}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                        <Assignment />
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Zoom>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Pending Approval
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                          {statistics.pending}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                        <HourglassEmpty />
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Zoom>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Awaiting Issuance
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1} color="info.main">
                          {statistics.approved}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                        <LocalShipping />
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Zoom>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Completed
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                          {statistics.completed}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                        <DoneAll />
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Zoom>
            </Grid>
          </Grid>

          {/* Main Content Paper */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    <Receipt sx={{ mr: 1, verticalAlign: 'middle', fontSize: 28 }} />
                    Department Requisitions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View all requisitions from your department
                  </Typography>
                  {canMakeRequisition() && (
                    <Chip 
                      label={`Department: ${userDepartment}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
                <GlowingButton
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAddDialog}
                  disabled={!canMakeRequisition()}
                  sx={{
                    background: !canMakeRequisition() 
                      ? theme.palette.grey[500]
                      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  }}
                >
                  New Requisition
                </GlowingButton>
              </Box>
            </Box>

            {/* Search and Filters Bar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <GlowingButton
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExportToExcel}
                    >
                      Export
                    </GlowingButton>
                    
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      fetchRequisitions();
                    }}>
                      <Refresh />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Table */}
            {renderRequisitionsTable()}
          </Paper>
        </Box>
      </Box>

      {/* Add Requisition Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={handleCloseAddDialog} 
        maxWidth="md" 
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Add color="primary" />
            <Typography variant="h6" component="span" fontWeight={600}>
              New Requisition
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Department"
                value={userDepartment}
                disabled
                helperText="Department auto-set from your profile"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Requested By"
                value={loggedInUser}
                disabled
                helperText="Auto-filled from logged-in user"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                options={itemsList}
                getOptionLabel={(option) => option.item_name}
                onChange={handleItemSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Item"
                    placeholder="Search or select an item..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Inventory color="action" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>{option.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Category: {option.category} | Unit: {option.unit_of_measurement}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            
            {/* Stock Information Display */}
            {stockLoading && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Fetching stock information...
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {stockError && (
              <Grid item xs={12}>
                <Alert severity="error" icon={<ErrorOutline />}>
                  {stockError}
                </Alert>
              </Grid>
            )}
            
            {stockInfo && !stockLoading && (
              <Grid item xs={12}>
                <Fade in={true}>
                  <StockIndicator stocklevel={getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold)}>
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" fontWeight={600} display="flex" alignItems="center" gap={0.5}>
                          <Warehouse fontSize="small" />
                          Current Stock Status
                        </Typography>
                        <Chip 
                          size="small"
                          label={getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'low' ? 'Low Stock' : 
                                 getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'medium' ? 'Medium Stock' : 'In Stock'}
                          color={getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'low' ? 'error' :
                                 getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'medium' ? 'warning' : 'success'}
                        />
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Available Stock</Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {stockInfo.available_stock.toLocaleString()} <Typography component="span" variant="caption">{stockInfo.unit_of_measurement}</Typography>
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((stockInfo.available_stock / (stockInfo.lowstock_threshold * 3)) * 100, 100)}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                            color={getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'low' ? 'error' :
                                   getStockLevel(stockInfo.available_stock, stockInfo.lowstock_threshold) === 'medium' ? 'warning' : 'success'}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Storage Location</Typography>
                          <Typography variant="body2" fontWeight={500} display="flex" alignItems="center" gap={0.5}>
                            <LocationOn fontSize="small" color="action" />
                            {stockInfo.storage_location || 'Not specified'}
                          </Typography>
                        </Grid>
                        {stockInfo.lowstock_threshold > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Low Stock Threshold: {stockInfo.lowstock_threshold} {stockInfo.unit_of_measurement}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </StockIndicator>
                </Fade>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Quantity Requested"
                value={formData.quantity_requested}
                onChange={handleQuantityChange}
                error={!!formErrors.quantity_requested}
                helperText={formErrors.quantity_requested}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TrendingUp color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.001 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Unit of Measurement</InputLabel>
                <Select
                  value={formData.unit_of_measurement}
                  onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}
                  label="Unit of Measurement"
                  startAdornment={
                    <InputAdornment position="start">
                      <Category color="action" fontSize="small" />
                    </InputAdornment>
                  }
                >
                  {unitOptions.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes / Purpose"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Reason for requisition..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Stock Warning Messages */}
            {stockInfo && formData.quantity_requested && parseFloat(formData.quantity_requested) > 0 && (
              <Grid item xs={12}>
                {parseFloat(formData.quantity_requested) > stockInfo.available_stock ? (
                  <Alert severity="error" icon={<ErrorOutline />}>
                    <Typography variant="body2" fontWeight={500}>
                      Insufficient Stock!
                    </Typography>
                    <Typography variant="caption">
                      Requested: {formData.quantity_requested} {formData.unit_of_measurement} | 
                      Available: {stockInfo.available_stock} {stockInfo.unit_of_measurement}
                    </Typography>
                  </Alert>
                ) : (stockInfo.available_stock - parseFloat(formData.quantity_requested)) <= stockInfo.lowstock_threshold ? (
                  <Alert severity="warning" icon={<Warning />}>
                    <Typography variant="body2" fontWeight={500}>
                      Low Stock Warning!
                    </Typography>
                    <Typography variant="caption">
                      This requisition will reduce stock below the low stock threshold. Please consider reordering.
                    </Typography>
                  </Alert>
                ) : null}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseAddDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddRequisition} 
            disabled={loading || !!formErrors.quantity_requested || (stockInfo && parseFloat(formData.quantity_requested) > stockInfo.available_stock)}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit Requisition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Requisition Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span" fontWeight={600}>
              Requisition Details #{selectedRequisition?.id}
            </Typography>
            <StatusChip label={selectedRequisition?.status} status={selectedRequisition?.status} size="small" />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequisition && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Request Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.department}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Quantity Requested</Typography>
                  <Typography variant="body1">
                    {Number(selectedRequisition.quantity_requested).toLocaleString()} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">{selectedRequisition.requested_by}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1">{selectedRequisition.request_date}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1">{selectedRequisition.updated_at || selectedRequisition.request_date}</Typography>
                </Grid>
                {selectedRequisition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes / Purpose</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                      <Typography variant="body2">{selectedRequisition.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Approval Workflow
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_dept_head ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <ThumbUp sx={{ fontSize: 32, color: selectedRequisition.approved_by_dept_head ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Dept Head Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_dept_head || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_GM ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <Verified sx={{ fontSize: 32, color: selectedRequisition.approved_by_GM ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">GM Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_GM || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.issued_by ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <LocalShipping sx={{ fontSize: 32, color: selectedRequisition.issued_by ? 'primary.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Issued By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.issued_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.received_by ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05) }}>
                    <AssignmentTurnedIn sx={{ fontSize: 32, color: selectedRequisition.received_by ? 'success.main' : 'warning.main', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Received By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.received_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button 
            variant="outlined" 
            startIcon={<Print />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleCloseActionDialog}>
        <DialogTitle>Confirm Receipt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to confirm receipt of these items?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Item: {selectedRequisition?.item_name}<br />
            Quantity: {selectedRequisition?.quantity_requested} {selectedRequisition?.unit_of_measurement}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            By confirming receipt, you acknowledge that you have received the items in good condition.
            This action will be recorded with your name: {loggedInUser}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleUpdateStatus}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequisitionManagement;